import React, { Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Rute-bevisst ErrorBoundary: nullstilles automatisk når URL-en endrer seg,
 * slik at en krasj på én side ikke låser hele appen til man laster på nytt.
 */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}
import DocumentTitleUpdater from './components/DocumentTitleUpdater';
import ServerStatusBanner from './components/ServerStatusBanner';
import ProtectedRoute from './components/ProtectedRoute';
import AppShell from './components/layout/AppShell';
import RootGate from './components/RootGate';
import TittiChatWidget from './components/TittiChatWidget';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';

// Eager-loaded: Login + Dashboard (first pages users see)
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';

/**
 * lazyWithReload: som React.lazy, men hvis en chunk feiler å laste (typisk
 * etter en ny deploy der fil-hashene er endret og brukeren har en gammel
 * index.html i cache/CDN), lastes siden automatisk på nytt ÉN gang for å hente
 * den nye builden. Hindrer «ChunkLoadError» / blank skjerm for brukere som har
 * en åpen fane når vi deployer.
 */
function lazyWithReload(factory) {
  return React.lazy(() =>
    factory()
      .then((mod) => {
        try { window.sessionStorage.removeItem('chunk_reload_attempted'); } catch (e) {}
        return mod;
      })
      .catch((err) => {
        const isChunkError =
          err && (err.name === 'ChunkLoadError' ||
                  /Loading chunk [\w-]+ failed/i.test(err.message || ''));
        let attempted = false;
        try { attempted = !!window.sessionStorage.getItem('chunk_reload_attempted'); } catch (e) {}
        if (isChunkError && !attempted) {
          try { window.sessionStorage.setItem('chunk_reload_attempted', '1'); } catch (e) {}
          window.location.reload();
          return { default: () => null };  // tom komponent mens reload skjer
        }
        throw err;
      })
  );
}

// Lazy-loaded: all other pages
const Setup = lazyWithReload(() => import('./pages/Setup'));
const Suppliers = lazyWithReload(() => import('./pages/Suppliers'));
const AttributterKonsolidert = lazyWithReload(() => import('./pages/AttributterKonsolidert'));
const ProductImport = lazyWithReload(() => import('./pages/ProductImport'));
const GarnpakkerImport = lazyWithReload(() => import('./pages/GarnpakkerImport'));
const ImportSelector = lazyWithReload(() => import('./pages/ImportSelector'));
const ImportRapporter = lazyWithReload(() => import('./pages/ImportRapporter'));
const WebImportWizard = lazyWithReload(() => import('./pages/WebImportWizard'));
const TilbehorImport = lazyWithReload(() => import('./pages/TilbehorImport'));
const SettingsPage = lazyWithReload(() => import('./pages/SettingsPage'));
const TemplateManager = lazyWithReload(() => import('./pages/TemplateManager'));
const Results = lazyWithReload(() => import('./pages/Results'));
const Profile = lazyWithReload(() => import('./pages/Profile'));
const SupplierImageManager = lazyWithReload(() => import('./pages/SupplierImageManager'));
const Varemottak = lazyWithReload(() => import('./pages/Varemottak'));
const Mottakskvittering = lazyWithReload(() => import('./pages/Mottakskvittering'));
const Ordrebekreftelse = lazyWithReload(() => import('./pages/Ordrebekreftelse'));
const VarerIBestilling = lazyWithReload(() => import('./pages/VarerIBestilling'));
const Kundebestilling = lazyWithReload(() => import('./pages/Kundebestilling'));
const Vaktplan = lazyWithReload(() => import('./pages/Vaktplan'));
const OpprettOrdre = lazyWithReload(() => import('./pages/OpprettOrdre'));
const Salg = lazyWithReload(() => import('./pages/Salg'));
const Salgsrapport = lazyWithReload(() => import('./pages/Salgsrapport'));
const ImporterVarer = lazyWithReload(() => import('./pages/ImporterVarer'));
const Synkroniseringsmaler = lazyWithReload(() => import('./pages/Synkroniseringsmaler'));
const Produktmaster = lazyWithReload(() => import('./pages/Produktmaster'));
const ProduktmasterHub = lazyWithReload(() => import('./pages/ProduktmasterHub'));
const NyheterDashboard = lazyWithReload(() => import('./pages/NyheterDashboard'));
const MediaBehandling = lazyWithReload(() => import('./pages/MediaBehandling'));
const AgentNotater = lazyWithReload(() => import('./pages/AgentNotater'));
const MissingFieldsChecker = lazyWithReload(() => import('./pages/MissingFieldsChecker'));
const SandnesExcelImport = lazyWithReload(() => import('./pages/SandnesExcelImport'));
const RaumaExcelImport = lazyWithReload(() => import('./pages/RaumaExcelImport'));
const HoyExcelImport = lazyWithReload(() => import('./pages/HoyExcelImport'));
const HillesvagExcelImport = lazyWithReload(() => import('./pages/HillesvagExcelImport'));
const VikingExcelImport = lazyWithReload(() => import('./pages/VikingExcelImport'));
const FilcolanaExcelImport = lazyWithReload(() => import('./pages/FilcolanaExcelImport'));
const IsagerExcelImport = lazyWithReload(() => import('./pages/IsagerExcelImport'));
const PerminExcelImport = lazyWithReload(() => import('./pages/PerminExcelImport'));
const VillyExcelImport = lazyWithReload(() => import('./pages/VillyExcelImport'));
const KortOgGodtExcelImport = lazyWithReload(() => import('./pages/KortOgGodtExcelImport'));
const ExcelImportHub = lazyWithReload(() => import('./pages/ExcelImportHub'));
const LeverandorBeskjeder = lazyWithReload(() => import('./pages/LeverandorBeskjeder'));
const Huskeliste = lazyWithReload(() => import('./pages/Huskeliste'));
const MessebestillingDetail = lazyWithReload(() => import('./pages/MessebestillingDetail'));
const ExcelImport = lazyWithReload(() => import('./pages/ExcelImport'));
const ProductKobling = lazyWithReload(() => import('./pages/ProductKobling'));
const WooCommerceSync = lazyWithReload(() => import('./pages/WooCommerceSync'));
const ConflictResolver = lazyWithReload(() => import('./pages/ConflictResolver'));
const UniversalMatching = lazyWithReload(() => import('./pages/UniversalMatching'));
const Varetelling = lazyWithReload(() => import('./pages/Varetelling'));
const PeriodiskVaretelling = lazyWithReload(() => import('./pages/PeriodiskVaretelling'));
const EpostInnboks = lazyWithReload(() => import('./pages/EpostInnboks'));
const DataIntegrity = lazyWithReload(() => import('./pages/DataIntegrity'));
const Publiseringsplan = lazyWithReload(() => import('./pages/Publiseringsplan'));
const LeverandorOppsett = lazyWithReload(() => import('./pages/LeverandorOppsett'));
const KasseSamspill = lazyWithReload(() => import('./pages/KasseSamspill'));
const Lagerstyring = lazyWithReload(() => import('./pages/Lagerstyring'));
const FirstUserSetup = lazyWithReload(() => import('./pages/FirstUserSetup'));
const SyncUpdate = lazyWithReload(() => import('./pages/import/SyncUpdate'));
const SyncDashboard = lazyWithReload(() => import('./pages/SyncDashboard'));
const OrdersList = lazyWithReload(() => import('./pages/orders/OrdersList'));
const InventoryOverview = lazyWithReload(() => import('./pages/inventory/InventoryOverview'));
const VarebehandlingHub = lazyWithReload(() => import('./pages/VarebehandlingHub'));
const OrdrebehandlingHub = lazyWithReload(() => import('./pages/OrdrebehandlingHub'));
const InnstillingerHub = lazyWithReload(() => import('./pages/InnstillingerHub'));
const InnstillingerWoo = lazyWithReload(() => import('./pages/InnstillingerWoo'));
const DropboxSettings = lazyWithReload(() => import('./pages/DropboxSettings'));
const InnstillingerPck = lazyWithReload(() => import('./pages/InnstillingerPck'));
const SynkroniseringHub = lazyWithReload(() => import('./pages/SynkroniseringHub'));
const Oppskriftsbibliotek = lazyWithReload(() => import('./pages/Oppskriftsbibliotek'));
const OppskriftDetalj = lazyWithReload(() => import('./pages/OppskriftDetalj'));
const OppskriftInnsjekk = lazyWithReload(() => import('./pages/OppskriftInnsjekk'));
const AdminLayout = lazyWithReload(() => import('./pages/admin/AdminLayout'));
const AdminOverview = lazyWithReload(() => import('./pages/admin/AdminOverview'));
const AdminUsers = lazyWithReload(() => import('./pages/admin/AdminUsers'));
const AdminWaitlist = lazyWithReload(() => import('./pages/admin/AdminWaitlist'));
const AdminLandingContent = lazyWithReload(() => import('./pages/admin/AdminLandingContent'));
const AdminHealth = lazyWithReload(() => import('./pages/admin/AdminHealth'));
const AdminErrors = lazyWithReload(() => import('./pages/admin/AdminErrors'));
const AdminSecurity = lazyWithReload(() => import('./pages/AdminSecurity'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      <span className="text-sm text-slate-500">Laster...</span>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ServerStatusBanner />
        <div className="App">
          <BrowserRouter>
            <DocumentTitleUpdater />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<RootGate />} />
                <Route path="/login" element={<Login />} />
                <Route path="/setup-first-user" element={<FirstUserSetup />} />

                {/* All other routes require authentication */}
                <Route path="/*" element={
                  <ProtectedRoute>
                    <AppShell>
                      <RouteErrorBoundary>
                      <Routes>

                      {/* Hub pages */}
                      <Route path="/varebehandling" element={<VarebehandlingHub />} />
                      <Route path="/ordrebehandling" element={<OrdrebehandlingHub />} />
                      {/* Bestillingsliste er fjernet — send gamle lenker til hub */}
                      <Route path="/bestillingsliste" element={<Navigate to="/ordrebehandling" replace />} />
                      <Route path="/innstillinger" element={<InnstillingerHub />} />
                      <Route path="/innstillinger/dropbox" element={<DropboxSettings />} />
                      <Route path="/innstillinger/pck" element={<InnstillingerPck />} />
                      <Route path="/innstillinger/woocommerce" element={<InnstillingerWoo />} />
                      <Route path="/leverandor-oppsett" element={<LeverandorOppsett />} />
                      <Route path="/synkronisering" element={<SynkroniseringHub />} />

                      {/* Varebehandling routes */}
                      <Route path="/import" element={<ImportSelector />} />
                      <Route path="/import/rapporter" element={<ImportRapporter />} />
                      <Route path="/import/web" element={<WebImportWizard />} />
                      <Route path="/import/tilbehor" element={<TilbehorImport />} />
                      <Route path="/import/excel" element={<ProductImport />} />
                      <Route path="/product-kobling" element={<ProductKobling />} />
                      <Route path="/publiseringsplan" element={<Publiseringsplan />} />
                      <Route path="/product-import" element={<ProductImport />} />
                      <Route path="/garnpakker" element={<GarnpakkerImport />} />
                      <Route path="/garnpakker/import" element={<GarnpakkerImport />} />
                      <Route path="/excel-import" element={<ExcelImportHub />} />
                      <Route path="/sandnes-import" element={<SandnesExcelImport />} />
                      <Route path="/rauma-import" element={<RaumaExcelImport />} />
                      <Route path="/hoy-import" element={<HoyExcelImport />} />
                      <Route path="/hillesvag-import" element={<HillesvagExcelImport />} />
                      <Route path="/viking-import" element={<VikingExcelImport />} />
                      <Route path="/filcolana-import" element={<FilcolanaExcelImport />} />
                      <Route path="/isager-import" element={<IsagerExcelImport />} />
                      <Route path="/permin-import" element={<PerminExcelImport />} />
                      <Route path="/villy-import" element={<VillyExcelImport />} />
                      <Route path="/kortoggodt-import" element={<KortOgGodtExcelImport />} />
                      <Route path="/varemottak" element={<Varemottak />} />
                      <Route path="/varemottak/kvittering/:orderId" element={<Mottakskvittering />} />
                      <Route path="/ordrebekreftelse" element={<Ordrebekreftelse />} />
                      <Route path="/varer-i-bestilling" element={<VarerIBestilling />} />
                      <Route path="/kundebestilling" element={<Kundebestilling />} />
                      <Route path="/vaktplan" element={<Vaktplan />} />
                      <Route path="/opprett-ordre/:supplierId" element={<OpprettOrdre />} />
                      <Route path="/salg" element={<Salg />} />
                      <Route path="/salgsrapport" element={<Salgsrapport />} />
                      <Route path="/leverandor-beskjeder" element={<LeverandorBeskjeder />} />
                      <Route path="/huskeliste" element={<Huskeliste />} />
                      <Route path="/messebestilling/:id" element={<MessebestillingDetail />} />
                      <Route path="/importer-varer" element={<ImporterVarer />} />
                      <Route path="/synkroniseringsmaler" element={<Synkroniseringsmaler />} />
                      <Route path="/produktmaster" element={<ProduktmasterHub />} />
                      <Route path="/attributes" element={<AttributterKonsolidert />} />
                      <Route path="/mapping" element={<AttributterKonsolidert />} />
                      <Route path="/results/:jobId" element={<Results />} />

                      {/* Supplier Monitoring routes */}
                      <Route path="/nyheter" element={<NyheterDashboard />} />
                      <Route path="/agent-notater" element={<AgentNotater />} />
                      <Route path="/missing-fields" element={<MissingFieldsChecker />} />
                      <Route path="/woo-sync" element={<WooCommerceSync />} />
                      <Route path="/media-behandling" element={<MediaBehandling />} />
                      <Route path="/conflicts" element={<ConflictResolver />} />
                      <Route path="/data-integrity" element={<DataIntegrity />} />
                      <Route path="/oppskriftsbibliotek" element={<Oppskriftsbibliotek />} />
                      <Route path="/oppskriftsbibliotek/:id" element={<OppskriftDetalj />} />
                      <Route path="/oppskrift-innsjekk" element={<OppskriftInnsjekk />} />

                      {/* Ordrebehandling routes */}
                      <Route path="/orders" element={<OrdersList />} />

                      {/* Innstillinger routes */}
                      <Route path="/templates" element={<TemplateManager />} />
                      <Route path="/supplier-images" element={<SupplierImageManager />} />
                      <Route path="/settings" element={<SettingsPage />} />

                      <Route path="/matching" element={<UniversalMatching />} />
                      <Route path="/varetelling" element={<Varetelling />} />
                      <Route path="/periodisk-varetelling" element={<PeriodiskVaretelling />} />
                      <Route path="/epost-innboks" element={<EpostInnboks />} />
                      <Route path="/kasse-samspill" element={<KasseSamspill />} />
                      <Route path="/lagerstyring" element={<Lagerstyring />} />

                      {/* Profil routes */}
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/setup" element={<Setup />} />
                      <Route path="/suppliers" element={<Suppliers />} />

                      {/* Admin section (egen ruteside med faner) */}
                      <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminOverview />} />
                        <Route path="brukere" element={<AdminUsers />} />
                        <Route path="venteliste" element={<AdminWaitlist />} />
                        <Route path="innhold" element={<AdminLandingContent />} />
                        <Route path="helse" element={<AdminHealth />} />
                        <Route path="feilrapport" element={<AdminErrors />} />
                        <Route path="feil" element={<AdminErrors />} />
                      </Route>
                      <Route path="/admin/security" element={<AdminSecurity />} />

                      {/* Future features */}
                      <Route path="/sync-update" element={<SyncUpdate />} />
                      <Route path="/sync-dashboard" element={<SyncDashboard />} />
                      <Route path="/inventory" element={<InventoryOverview />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </RouteErrorBoundary>
                    </AppShell>
                  </ProtectedRoute>
                } />
              </Routes>
            </Suspense>
            <Toaster />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
